import type { editor, IPosition, IRange } from "monaco-editor";

import { Monaco } from "@monaco-editor/react";
import { useCallback, useRef, useState } from "react";

import {
  calculatePickerPosition,
  insertTextAtRange,
  setupContextMenuActions,
  setupPickerTriggers,
} from "../utils/monaco-editor-helpers";

export type PickerType = "date" | "entity" | "liquid";

export interface PickerState {
  left: number;
  open: boolean;
  range: IRange | null;
  top: number;
  type: PickerType;
}

export interface UseMonacoPickerReturn {
  closePicker: () => void;
  editorRef: React.MutableRefObject<editor.IStandaloneCodeEditor | null>;
  handleEditorMount: (
    editorInstance: editor.IStandaloneCodeEditor,
    monacoInstance: Monaco,
  ) => void;
  insertText: (text: string, range: IRange | null) => void;
  monacoRef: React.MutableRefObject<Monaco | null>;
  openPickerAtPosition: (
    editorInstance: editor.IStandaloneCodeEditor,
    position: IPosition,
    range: IRange,
    type: PickerType,
  ) => void;
  pickerState: PickerState;
  setupContextMenu: (
    editorInstance: editor.IStandaloneCodeEditor,
    monacoInstance: Monaco,
  ) => void;
  setupTriggers: (
    editorInstance: editor.IStandaloneCodeEditor,
    monacoInstance: Monaco,
  ) => () => void;
}

/**
 * Custom hook for managing Monaco editor picker state and operations
 */
export const useMonacoPicker = (): UseMonacoPickerReturn => {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);

  const [pickerState, setPickerState] = useState<PickerState>({
    left: 0,
    open: false,
    range: null,
    top: 0,
    type: "entity",
  });

  /**
   * Opens a picker at the specified position
   */
  const openPickerAtPosition = useCallback(
    (
      editorInstance: editor.IStandaloneCodeEditor,
      position: IPosition,
      range: IRange,
      type: PickerType,
    ) => {
      const coords = calculatePickerPosition(editorInstance, position);
      if (!coords) return;

      setPickerState({
        left: coords.left,
        open: true,
        range,
        top: coords.top,
        type,
      });
    },
    [],
  );

  /**
   * Closes the picker
   */
  const closePicker = useCallback(() => {
    setPickerState((s) => ({ ...s, open: false }));
  }, []);

  /**
   * Inserts text at the specified range and closes the picker
   */
  const insertText = useCallback(
    (text: string, range: IRange | null) => {
      const editorInstance = editorRef.current;
      if (!editorInstance || !range) return;

      insertTextAtRange(editorInstance, text, range);
      closePicker();
    },
    [closePicker],
  );

  /**
   * Sets up trigger detection for entity (<<) and date ({{) pickers
   */
  const setupTriggers = useCallback(
    (editorInstance: editor.IStandaloneCodeEditor, monacoInstance: Monaco) => {
      return setupPickerTriggers(editorInstance, monacoInstance, {
        isPickerOpen: () => pickerState.open,
        onTrigger: openPickerAtPosition,
      });
    },
    [openPickerAtPosition, pickerState.open],
  );

  /**
   * Sets up context menu actions for inserting entities and date snippets
   */
  const setupContextMenu = useCallback(
    (editorInstance: editor.IStandaloneCodeEditor, monacoInstance: Monaco) => {
      setupContextMenuActions(editorInstance, monacoInstance, {
        onOpenPicker: openPickerAtPosition,
      });
    },
    [openPickerAtPosition],
  );

  /**
   * Handles editor mount - stores refs and sets up triggers/context menu
   */
  const handleEditorMount = useCallback(
    (editorInstance: editor.IStandaloneCodeEditor, monacoInstance: Monaco) => {
      editorRef.current = editorInstance;
      monacoRef.current = monacoInstance;
    },
    [],
  );

  return {
    closePicker,
    editorRef,
    handleEditorMount,
    insertText,
    monacoRef,
    openPickerAtPosition,
    pickerState,
    setupContextMenu,
    setupTriggers,
  };
};
